import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * A saved card drawn like a payment card (metadata only: bank, network, last 4, holder, expiry).
 * The colour is picked from the bank + last 4, so two cards of the same network still look different;
 * an expired card is drawn faded with an EXPIRED tag.
 */

const PALETTES = [
  ['#1f4e79', '#3a7bbf'], // blue
  ['#2d2a4a', '#5b4b8a'], // indigo
  ['#0f5e57', '#2a9d8f'], // teal
  ['#7a2e3a', '#c0504d'], // wine
  ['#3b3b3b', '#6b6b6b'], // graphite
  ['#7a4b12', '#c98a2b'], // bronze
  ['#164a2e', '#3f8f5f'], // green
  ['#4a1f5c', '#8e4fb3'], // purple
];

// MM/YY before the current month
export const isCardExpired = (expiry) => {
  const m = /^(\d{2})\/(\d{2})$/.exec(expiry || '');
  if (!m) return false;
  const now = new Date();
  const yy = now.getFullYear() % 100;
  const mm = now.getMonth() + 1;
  return Number(m[2]) < yy || (Number(m[2]) === yy && Number(m[1]) < mm);
};

const hash = (text) => Array.from(text || '').reduce((h, ch) => ((h * 31) + ch.charCodeAt(0)) >>> 0, 7);
export const paletteFor = (card) => PALETTES[hash(`${card.bankIssuer || ''}|${card.lastFour || ''}|${card.cardType || ''}`) % PALETTES.length];

const NetworkMark = ({ type }) => {
  if (type === 'MASTERCARD') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }} aria-label="Mastercard">
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#eb001b' }} />
        <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#f79e1b', ml: '-7px', opacity: 0.9 }} />
      </Box>
    );
  }
  if (type === 'AMEX') {
    return (
      <Box sx={{ px: 0.75, py: 0.25, border: '1.5px solid rgba(255,255,255,0.9)', borderRadius: 0.5 }}>
        <Typography sx={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: 1, color: '#fff', lineHeight: 1.2 }}>AMEX</Typography>
      </Box>
    );
  }
  return (
    <Typography sx={{ fontSize: '1rem', fontWeight: 900, fontStyle: 'italic', color: '#fff', letterSpacing: 0.5, lineHeight: 1 }}>
      {type === 'VISA' ? 'VISA' : type}
    </Typography>
  );
};

const Chip = () => (
  <Box
    aria-hidden
    sx={{
      width: 28, height: 21, borderRadius: '4px',
      background: 'linear-gradient(135deg, #e9d18b 0%, #c9a74a 50%, #f0dc9c 100%)',
      position: 'relative', overflow: 'hidden',
      '&::before, &::after': { content: '""', position: 'absolute', bgcolor: 'rgba(0,0,0,0.18)' },
      '&::before': { left: 0, right: 0, top: '50%', height: '1px' },
      '&::after': { top: 0, bottom: 0, left: '50%', width: '1px' },
    }}
  />
);

const CardFace = ({ card, expired, topRight }) => {
  const [from, to] = expired ? ['#5f6368', '#8d9196'] : paletteFor(card);
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        aspectRatio: '1.586',
        maxWidth: 280,
        borderRadius: 2,
        p: 1.5,
        color: '#fff',
        background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)`,
        boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        // soft circles for depth
        '&::before': {
          content: '""', position: 'absolute', width: '70%', aspectRatio: '1', borderRadius: '50%',
          right: '-25%', top: '-45%', bgcolor: 'rgba(255,255,255,0.08)',
        },
        '&::after': {
          content: '""', position: 'absolute', width: '55%', aspectRatio: '1', borderRadius: '50%',
          left: '-20%', bottom: '-50%', bgcolor: 'rgba(255,255,255,0.06)',
        },
      }}
    >
      {/* bank + extras */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, position: 'relative', zIndex: 1 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.3, flex: 1, minWidth: 0 }} noWrap>
          {card.bankIssuer || 'Card'}
        </Typography>
        {expired && (
          <Box sx={{ bgcolor: '#d32f2f', borderRadius: 0.75, px: 0.75, py: 0.125}}>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 800, letterSpacing: 1, color: '#fff' }}>EXPIRED</Typography>
          </Box>
        )}
        {topRight}
      </Box>

      <Box sx={{ mt: 1, position: 'relative', zIndex: 1 }}><Chip /></Box>

      {/* number: last 4 only */}
      <Typography
        sx={{ mt: 'auto', mb: 0.5, fontFamily: 'monospace', fontSize: '0.875rem', letterSpacing: 1.5, position: 'relative', zIndex: 1 }}
        aria-label={`Card ending ${card.lastFour}`}
      >
        •••• •••• •••• {card.lastFour}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, position: 'relative', zIndex: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.5rem', opacity: 0.75, letterSpacing: 0.5 }}>CARDHOLDER</Typography>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }} noWrap>
            {card.cardholderName || '—'}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.5rem', opacity: 0.75, letterSpacing: 0.5 }}>EXPIRES</Typography>
          <Typography sx={{ fontSize: '0.6875rem', fontWeight: 600 }}>{card.expiry || '—'}</Typography>
        </Box>
        <NetworkMark type={card.cardType} />
      </Box>

    </Box>
  );
};

export default CardFace;
